import { IExtendedSearchData } from "src/api/server/extended-search.types";
import { SAVINGS_PLAN_TAG } from "src/components/pages/search/constants";
import { SearchQueryParams } from "src/components/pages/search/search.types";
import { isRankingAvailable } from "src/components/pages/search/utils/is-ranking-available";
import { ICountry, LOCALE_PARAMETERS } from "src/constants/locale";
import { SortDirection } from "src/constants/sort-direction";
import { EnumCountryKey } from "src/types/common-enum";
import { parseUrlQueryToUrlSearchParams, getNormalizedUrlParams, getCanonicalUrlParams, removeRedundantCommas } from "./url-params-helpers";

const removeInvestableParamIfNeeded = (fixedQueryParams: URLSearchParams, country: ICountry): void => {
    const shouldRemoveIsInvestable = fixedQueryParams.get(SearchQueryParams.IsInvestable.toLowerCase()) && country === EnumCountryKey.INT;
    const shouldRemoveIsAtInvestable = fixedQueryParams.get(SearchQueryParams.IsAtInvestable.toLowerCase()) && country !== EnumCountryKey.AT;
    const shouldRemoveIsChInvestable = fixedQueryParams.get(SearchQueryParams.IsChInvestable.toLowerCase()) && country !== EnumCountryKey.CH;

    if (shouldRemoveIsInvestable) {
        fixedQueryParams.delete(SearchQueryParams.IsInvestable.toLowerCase());
    }
    if (shouldRemoveIsAtInvestable) {
        fixedQueryParams.delete(SearchQueryParams.IsAtInvestable.toLowerCase());
    }
    if (shouldRemoveIsChInvestable) {
        fixedQueryParams.delete(SearchQueryParams.IsChInvestable.toLowerCase());
    }
};

const removeSavingsPlanTagIfNeeded = (fixedQueryParams: URLSearchParams): void => {
    const tagsQuery = fixedQueryParams.get(SearchQueryParams.Tags.toLowerCase());
    if (!tagsQuery?.includes(SAVINGS_PLAN_TAG)) {
        return;
    }

    const tagsWithoutSavingPlan = tagsQuery.replace(SAVINGS_PLAN_TAG, "");
    const newTagsQuery = removeRedundantCommas(tagsWithoutSavingPlan);

    if (newTagsQuery) {
        fixedQueryParams.set(SearchQueryParams.Tags, newTagsQuery);
    } else {
        fixedQueryParams.delete(SearchQueryParams.Tags);
    }
};

const cleanupBrokerRelatedParams = (fixedQueryParams: URLSearchParams, hasBrokers: boolean): void => {
    if (hasBrokers) {
        return;
    }

    if (fixedQueryParams.get(SearchQueryParams.BrokerIds.toLowerCase())) {
        fixedQueryParams.delete(SearchQueryParams.BrokerIds.toLowerCase());
    }

    removeSavingsPlanTagIfNeeded(fixedQueryParams);
};

const resetSortIfNeeded = (fixedQueryParams: URLSearchParams, extendedSearchData: IExtendedSearchData): void => {
    const sortByKey = SearchQueryParams.SortByRanking.toLowerCase();
    const sortOrderKey = SearchQueryParams.SortDirection.toLowerCase();

    const sortBy = fixedQueryParams.get(sortByKey);
    if (!sortBy) {
        return;
    }

    const { rankings, relevanceRanking, defaultSelectedRanking } = extendedSearchData.rankingsData;
    const defaultFigureIds = new Set(extendedSearchData.figuresPicker.defaultFigures.map(figure => figure.id));
    const requestedRanking = rankings.find(ranking => ranking.urlIdentifier === sortBy);
    const isSortByAvailable = requestedRanking && isRankingAvailable(requestedRanking, relevanceRanking, defaultFigureIds);

    if (isSortByAvailable) {
        return;
    }

    if (defaultSelectedRanking.urlIdentifier) {
        fixedQueryParams.set(sortByKey, defaultSelectedRanking.urlIdentifier);
    } else {
        fixedQueryParams.delete(sortByKey);
    }

    if (defaultSelectedRanking.sortDirection !== null) {
        const sortOrder = defaultSelectedRanking.sortDirection === 1 ? SortDirection.Descending : SortDirection.Ascending;
        fixedQueryParams.set(sortOrderKey, sortOrder);
    } else {
        fixedQueryParams.delete(sortOrderKey);
    }
};

type TUrlQuery = Record<string, string | string[] | undefined>;

interface IFixSearchUrlQueryParams {
    query: TUrlQuery;
    country: ICountry;
    extendedSearchData: IExtendedSearchData;
}

export const fixSearchUrlQuery = ({ query, country, extendedSearchData }: IFixSearchUrlQueryParams) => {
    const urlSearchParams = parseUrlQueryToUrlSearchParams(query);
    const fixedQueryParams = getNormalizedUrlParams(urlSearchParams);

    LOCALE_PARAMETERS.forEach(param => fixedQueryParams.delete(param));

    removeInvestableParamIfNeeded(fixedQueryParams, country);
    cleanupBrokerRelatedParams(fixedQueryParams, !!extendedSearchData.tagsData.brokers?.length);
    resetSortIfNeeded(fixedQueryParams, extendedSearchData);

    const updatedQueryParams = getCanonicalUrlParams(fixedQueryParams);

    return updatedQueryParams;
};
